import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import Group from 'components/utils/Group'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import TextButton from 'LuxKit/Button/TextButton'
import { canCancelOrderItem, canExtendHotelDates, canRequestChangeHotelDates, getFreeCancellationPeriodEndDate } from 'supportAssistant/utils/orderUtils'
import LesaPurchaseSummaryImage from './Common/LesaPurchaseSummaryImage'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { buildChatMessageMetaPayload } from 'supportAssistant/utils/messageUtils'
import useOrderCancellationConfigs from 'hooks/Order/useOrderCancellationConfigs'
import { ChatActions } from '@luxuryescapes/contract-support'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import { extractOccupantsFromReservation } from 'lib/checkout/cartReservationUtils'
import { canAddExtraGuests, getExtraGuestsAvailabilityReservationId } from 'lib/order/extraGuestUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'

interface Props {
  item: App.OrderItem
  order: App.Order
  offer: App.Offer
  roomLabel: string
  hideSelectButton?: boolean
  isCurrentOrder?: boolean
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  /**
   * Overrides the default SELECT_ORDER_ITEM dispatch. Used when this card stands in for an
   * assistant selection pill, which posts the pill's own value back instead of a chat action.
   */
  onSelect?: () => void
}

function LesaHotelItemSummary(props: Props) {
  const {
    item,
    order,
    offer,
    roomLabel,
    hideSelectButton = false,
    isCurrentOrder,
    componentsMap,
    onSelect: onSelectOverride,
  } = props
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()

  const [cancellationConfig] = useOrderCancellationConfigs(item.id, order.id)
  const isSelfServiceNameChangeEnabled = useFeatureFlag(OptimizelyFeatureFlags.opexSelfServiceNameChange)

  const dispatch = useAppDispatch()

  const bookingNumber = item.bookingNumber
  const { rates } = useOfferAvailableRates(item?.offerId, {
    checkIn: item.reservation?.startDate,
    checkOut: item.reservation?.endDate,
    rooms: extractOccupantsFromReservation(item.reservation),
    reservationId: getExtraGuestsAvailabilityReservationId(item),
  })

  const canAddGuests = canAddExtraGuests(offer as App.HotelOffer, order, item, rates, { canRefund: cancellationConfig?.canRefund })

  const canChangeDates = !!cancellationConfig.canChangeDates
  const canRequestChangeDates = canRequestChangeHotelDates(order, offer, cancellationConfig)
  const canExtendDates = canChangeDates && canExtendHotelDates(order, offer, item.id, cancellationConfig)
  const canBeCancelled = canCancelOrderItem(order, item, cancellationConfig)
  const freeCancellationPeriodEndDate = getFreeCancellationPeriodEndDate(order, item, cancellationConfig) ?? ''

  const action: ChatActions = componentsMap?.get('list-order-items-extra-guests') ? ChatActions.SELECT_ORDER_ITEM_EXTRA_GUESTS : ChatActions.SELECT_ORDER_ITEM

  const onSelectDefault = useCallback(() => {
    dispatch(addChatMessage(item.bookingNumber, {
      action,
      payload: {
        ...buildChatMessageMetaPayload(order, item, offer, {
          isCurrentOrder,
          canRequestChangeDates,
          canExtendDates,
          checkInDate: item.reservation?.startDate,
          canChangeDates,
          canBeCancelled,
          freeCancellationPeriodEndDate,
          orderType: 'hotel',
          canAddExtraGuests: canAddGuests,
        }, cancellationConfig, isSelfServiceNameChangeEnabled),
      },
    }))
  }, [canAddGuests, canBeCancelled, canChangeDates, canExtendDates, canRequestChangeDates, dispatch, freeCancellationPeriodEndDate, isCurrentOrder, isSelfServiceNameChangeEnabled, item, offer, order, action, cancellationConfig])

  const onSelect = onSelectOverride ?? onSelectDefault

  // Need to convince the types it's a hotel item
  const pkg = 'roomTypeName' in item.package ? item.package : undefined
  const reservationStartDate = item.reservation?.startDate
    ? dateFormatter(item.reservation.startDate, INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR)
    : ''
  const reservationEndDate = item.reservation?.endDate
    ? dateFormatter(item.reservation.endDate, INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR)
    : ''

  if (!item || !pkg) {
    return null
  }

  return <LesaPurchaseSummaryContainer>
    <LesaPurchaseSummaryHeader order={order}>
      <StructureLabel variant="standard">{roomLabel}</StructureLabel>
    </LesaPurchaseSummaryHeader>
    <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
      <Group direction="vertical" gap={4}>
        <BodyText variant="medium" weight="bold">{pkg.name}</BodyText>
        <BodyText variant="medium">{pkg.roomTypeName}</BodyText>
        {bookingNumber && <BodyText variant="small" colour="light-foreground-alt"><strong>{intl.formatMessage({
          id: 'YjLVoUox1wvaPox9aFkk',
          defaultMessage: 'Booking ID:',
          description: 'Label for hotel item booking ID on the hotel or room booking page',
        })}
        </strong> {bookingNumber}
        </BodyText>}
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <LineUsersAltIcon colour="primary" size="XS" />
          <BodyText variant="medium" format="titlecase">
            {formatOccupantsShort(item.reservation)}
          </BodyText>
        </Group>
        {!!reservationStartDate && !!reservationEndDate && <Group direction="horizontal" gap={4} verticalAlign="center">
          <LineCalendarIcon size="XS" />
          <BodyText variant="medium">{reservationStartDate}</BodyText>
          <LineArrowRightIcon colour="light-foreground-alt" size="XS" />
          <BodyText variant="medium">{reservationEndDate}</BodyText>
        </Group>}
      </Group>
      <Group gap={16} direction="vertical">
        <LesaPurchaseSummaryImage id={pkg.roomTypeImage ?? item?.offer.image.id} />
        {!hideSelectButton && <TextButton onClick={onSelect} fit="mobile-full-width" size="small" kind="secondary">{intl.formatMessage({
          id: 'HxLoKDQxg3hlDticRjzn',
          defaultMessage: 'Select',
          description: 'Button to select a hotel room item on the hotel or room booking page',
        })}
        </TextButton>}
      </Group>
    </Group>
  </LesaPurchaseSummaryContainer>
}

export default LesaHotelItemSummary
